import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import html2pdf from 'html2pdf.js';

/**
 * useSharePdf — PDF GENERATION + the Download / Print / Share ACTIONS, with no
 * opinion about what renders them.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔀 EXTRACTED FROM `components/SharePdfButton.js` ON 2026-08-10. IT IS AN
 * EXTRACTION, NOT A REWRITE — EVERY LINE BELOW CAME OUT OF THAT COMPONENT.
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔑 **WHY, in one sentence: the product owner asked for Print inside the new
 * order actions menu, and a dropdown cannot live inside a dropdown here.** The
 * request was *"replace [the action row] with the single actions menu. We will
 * also include an export excell and print options in the action menu"* — so
 * `SharePdfButton`'s three actions have to be reachable from a panel that is
 * NOT `SharePdfButton`'s own panel. With only two routes to that — extract or
 * duplicate — the Code Unification Principle picks extract.
 *
 * 🪤 **NESTING `<OrderPdfButton>` INSIDE THE ACTIONS PANEL WAS THE OBVIOUS
 * CHEAP ANSWER AND IT IS BROKEN TWO INDEPENDENT WAYS. Both were checked
 * against the code, not guessed, and both are recorded here because the cheap
 * answer will look attractive again to the next reader:**
 *   1. **CLIP.** `SharePdfButton`'s menu is `absolute … z-50` inside its own
 *      `relative` wrapper. `components/ui/Dropdown.js`'s panel carries
 *      `max-h-[calc(100vh-5rem)] overflow-y-auto`. An absolutely-positioned
 *      child CANNOT escape an ancestor's `overflow` clip at any z-index —
 *      that is the whole measured lesson of the 2026-08-09 Scenes-picker
 *      defect recorded in `ui/Dropdown`'s docblock (panel 256×257 inside a
 *      120×49 cell, 0/5 probes visible, fixed only by lifting the clip). A
 *      nested PDF menu reproduces it exactly.
 *   2. **TWO CLOSE LISTENERS FIGHTING.** Both components close on a
 *      DOCUMENT-level listener when the event target is outside their own
 *      root — `ui/Dropdown` on `mousedown` (`:167`), `SharePdfButton` on
 *      `mousedown` (`:156`). Neither knows about the other, so the pair
 *      cannot be made to agree by ordering or by `stopPropagation`; whichever
 *      containment test fails first wins.
 * Extraction sidesteps both: the actions render as ordinary rows INSIDE the
 * one panel, so there is no second panel to clip and no second listener.
 *
 * 🚨 **`SharePdfButton` MUST KEEP RENDERING BYTE-IDENTICAL DOM.** It is not
 * the only caller and the others were not asked to change: `BudgetOverview.js`,
 * `BudgetCostCodes.js`, `BudgetComparePopout.js`, `RiskAssessmentDetail.js`
 * and `OrderPdfButton.js` (which is itself reached from `MyOrders.js`,
 * `OrderPage.js` ×2 and `DepartmentOrders.js`). So this hook deliberately
 * exposes the component's ENTIRE internal surface — including things the new
 * menu does not use, like `extras` / `activeExtras` / `nothingActive` — rather
 * than the subset today's second caller happens to need. Narrowing it later
 * would force the component to grow the logic back.
 *
 * ⚠️ **WHAT STAYED IN THE COMPONENT, and why it is the right seam:** the
 * `menuOpen` state and all JSX. `menuOpen` is a property of THAT disclosure —
 * the actions menu has its own open state, owned by `ui/Dropdown` — so a hook
 * that owned it would be handing each caller a piece of the other's UI.
 *
 * Options in === the component's props out. Returns:
 *   generating, pendingAction, activeExtras, setActiveExtras, extras,
 *   nothingActive, canShare, handleDownload, handlePrint, handleShare,
 *   resetCache
 *
 * `onActionComplete` is called after a completed Download/Share, and after a
 * Print whose generation SUCCEEDED. 🪤 It is deliberately NOT called on the
 * print-regeneration failure path: the original code `return`s there without
 * closing its menu or clearing its cache, leaving the user looking at the
 * control they just pressed. Preserved exactly — a failed action that silently
 * dismisses its own menu reads as a success.
 */
export default function useSharePdf({
  renderContent,
  filename,
  title,
  orientation = 'portrait',
  extras: extrasProp = null,
  extrasOptional = false,
  alternateRenderContent = null,
  alternateLabel = '',
  alternateFilenameSuffix = '',
  renderPdfMakeDoc = null,
  onActionComplete = null,
} = {}) {
  // Wrap the legacy props as a single-element extras list (key '__alt').
  // generatePdf below special-cases '__alt' to call alternateRenderContent
  // instead of renderContent.
  const legacyMode = !extrasProp && !!alternateRenderContent && !!alternateLabel;
  const extras = useMemo(() => {
    if (extrasProp) return extrasProp;
    if (legacyMode) {
      return [{ key: '__alt', label: alternateLabel, filenameSuffix: alternateFilenameSuffix }];
    }
    return [];
  }, [extrasProp, legacyMode, alternateLabel, alternateFilenameSuffix]);

  const [generating, setGenerating] = useState(false);
  // Which action row is currently generating ('download' | 'print' | 'share' |
  // null). Used to disable the other two action buttons + render an inline
  // "Generating…" hint next to the one the user clicked.
  const [pendingAction, setPendingAction] = useState(null);
  // Cache pdf blobs by variant key so toggling the same combination twice
  // doesn't regenerate. Cleared when the menu closes.
  // Shape: { [variantKey]: { blob, filename } }
  const [pdfCache, setPdfCache] = useState({});
  const [activeExtras, setActiveExtras] = useState(() => {
    const init = {};
    for (const x of extras) init[x.key] = !!x.defaultActive;
    return init;
  });
  // Re-seed when the extras list itself changes (e.g. Budget Overview's
  // Variance Analysis extra appears once Compare drawer has been opened).
  // Preserves any user-set values for keys that still exist; uses
  // `defaultActive` for newly-introduced keys.
  useEffect(() => {
    setActiveExtras((prev) => {
      const next = {};
      for (const x of extras) {
        next[x.key] = Object.prototype.hasOwnProperty.call(prev, x.key)
          ? prev[x.key]
          : !!x.defaultActive;
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extras.map((x) => x.key).join('|')]);
  const containerRef = useRef(null);

  const variantKey = useMemo(() => {
    const active = extras.filter((x) => activeExtras[x.key]).map((x) => x.key);
    return active.length === 0 ? 'default' : active.join('+');
  }, [extras, activeExtras]);
  // When extras are declared, at least one must be active for the PDF to
  // have anything to render — UNLESS `extrasOptional` says the base content
  // always renders on its own. Legacy mode (extras.length === 0) is always OK.
  const nothingActive = !extrasOptional && extras.length > 0 && !extras.some((x) => activeExtras[x.key]);

  const resetCache = useCallback(() => setPdfCache({}), []);

  const generatePdf = useCallback(async (extrasState) => {
    const baseName = filename || 'report.pdf';
    const suffix = extras
      .filter((x) => extrasState[x.key])
      .map((x) => x.filenameSuffix || '')
      .join('');
    const usedName = baseName.replace(/(\.pdf)?$/i, `${suffix}.pdf`);

    // pdfmake path — vector text PDF. Only taken when the caller's
    // renderPdfMakeDoc returns a non-null doc-def for this extras combination.
    // Returning null delegates to the html2pdf path below.
    if (renderPdfMakeDoc) {
      const docDef = renderPdfMakeDoc({ activeExtras: extrasState });
      if (docDef) {
        const [pdfMakeMod, pdfFontsMod] = await Promise.all([
          import('pdfmake/build/pdfmake'),
          import('pdfmake/build/vfs_fonts'),
        ]);
        const pdfMake = pdfMakeMod.default || pdfMakeMod;
        const pdfFonts = pdfFontsMod.default || pdfFontsMod;
        // Resolve the VFS across pdfmake 0.1/0.2/0.3.x export shapes — see
        // GradingExportButton for the established pattern.
        const vfs =
          (pdfFonts && pdfFonts.pdfMake && pdfFonts.pdfMake.vfs) ||
          (pdfFonts && pdfFonts.vfs) ||
          pdfFonts ||
          pdfMake.vfs;
        if (vfs) {
          if (typeof pdfMake.addVirtualFileSystem === 'function') {
            pdfMake.addVirtualFileSystem(vfs);
          } else {
            pdfMake.vfs = vfs;
          }
        }
        const pdf = pdfMake.createPdf(docDef);
        const blob = await new Promise((resolve, reject) => {
          const timer = setTimeout(() => reject(new Error('PDF render timed out')), 30000);
          try {
            pdf.getBlob((b) => { clearTimeout(timer); resolve(b); });
          } catch (e) { clearTimeout(timer); reject(e); }
        });
        return { blob, filename: usedName };
      }
    }

    // html2pdf path — raster image PDF.
    const useLegacyAlt = legacyMode && extrasState.__alt && alternateRenderContent;
    const contentFn = useLegacyAlt
      ? alternateRenderContent
      : () => renderContent({ activeExtras: extrasState });

    const container = document.createElement('div');
    const containerWidth = orientation === 'landscape' ? '297mm' : '210mm';
    container.style.cssText = `position:fixed;left:-9999px;top:0;width:${containerWidth};z-index:-1;`;
    document.body.appendChild(container);
    containerRef.current = container;

    const layoutWrapper = document.createElement('div');
    container.appendChild(layoutWrapper);
    const root = createRoot(layoutWrapper);
    root.render(contentFn());

    await new Promise(resolve => setTimeout(resolve, 150));

    const printLayout = container.querySelector('.pdf-print-layout');
    if (!printLayout) throw new Error('Print layout not found');

    const pdfBlob = await html2pdf()
      .set({
        margin: 0,
        filename: usedName,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation },
        pagebreak: { mode: 'css' }
      })
      .from(printLayout)
      .outputPdf('blob');

    root.unmount();
    document.body.removeChild(container);
    containerRef.current = null;

    return { blob: pdfBlob, filename: usedName };
  }, [renderContent, filename, orientation, extras, legacyMode, alternateRenderContent, renderPdfMakeDoc]);

  // Ensure pdfCache has an entry for the current variantKey. Regenerates if
  // missing. Returns the cached entry.
  const ensureCurrentVariant = useCallback(async () => {
    if (pdfCache[variantKey]) return pdfCache[variantKey];
    setGenerating(true);
    try {
      const next = await generatePdf(activeExtras);
      setPdfCache((prev) => ({ ...prev, [variantKey]: next }));
      return next;
    } finally {
      setGenerating(false);
    }
  }, [pdfCache, variantKey, generatePdf, activeExtras]);

  const finish = useCallback(() => {
    setPendingAction(null);
    if (typeof onActionComplete === 'function') onActionComplete();
    setPdfCache({});
  }, [onActionComplete]);

  const handleShare = useCallback(async () => {
    if (pendingAction) return;
    setPendingAction('share');
    try {
      const cur = await ensureCurrentVariant();
      const pdfFile = new File([cur.blob], cur.filename, { type: 'application/pdf' });
      await navigator.share({
        files: [pdfFile],
        title: title || 'Report',
        text: title || 'Report'
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('[SharePdf] Share failed:', err);
      }
    }
    finish();
  }, [ensureCurrentVariant, pendingAction, title, finish]);

  const handleDownload = useCallback(async () => {
    if (pendingAction) return;
    setPendingAction('download');
    try {
      const cur = await ensureCurrentVariant();
      const url = URL.createObjectURL(cur.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = cur.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[SharePdf] Download failed:', err);
    }
    finish();
  }, [ensureCurrentVariant, pendingAction, finish]);

  // Print via hidden iframe — uses the browser's native PDF preview + print
  // pipeline so the user gets the same layout as the downloaded PDF.
  const handlePrint = useCallback(async () => {
    if (pendingAction) return;
    setPendingAction('print');
    let cur;
    try {
      cur = await ensureCurrentVariant();
    } catch (err) {
      console.error('[SharePdf] Print regen failed:', err);
      // 🪤 Deliberately NOT `finish()` — see the docblock. The menu stays open
      // and the cache is left alone, so a failed print does not look like a
      // completed one.
      setPendingAction(null);
      return;
    }
    const url = URL.createObjectURL(cur.blob);
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    iframe.src = url;
    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (e) {
        console.error('[SharePdf] Print failed:', e);
      }
    };
    document.body.appendChild(iframe);
    setTimeout(() => {
      try { URL.revokeObjectURL(url); } catch {}
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    }, 60000);
    finish();
  }, [ensureCurrentVariant, pendingAction, finish]);

  const canShare = typeof navigator.canShare === 'function' &&
    navigator.canShare({ files: [new File([''], 'test.pdf', { type: 'application/pdf' })] });

  return {
    generating,
    pendingAction,
    activeExtras,
    setActiveExtras,
    extras,
    nothingActive,
    canShare,
    handleDownload,
    handlePrint,
    handleShare,
    resetCache,
  };
}
